/**
 * This function forces a function to wait a certain amount of time before running again.
 * So the function is built to limit the number of times a function is called.
 * @param {object} params This object contains two parameters
 * @param params.delay Delay time in milliseconds
 * @param params.immediate If immediate is true, the function triggers immediately and
 * then waits for the interval before being called again.
 * @param {function} callback This callback fn that you want to execute after the debounce time
 * @returns {function} debouncedFn
 * @example{
 *    const callbackFn = async () => {
 *        isFetching.value = true;
 *        const data = await getAllData(Promise.resolve(myData));
 *        isFetching.value = false;
 *    }
 *
 *   const getData = useDebounce({delay: 1000, immediate: true}, callbackFn);
 *
 *   result {
 *     getData = myData   // one second later this equality will be done
 *   }
 * }
 */
const useDebounce = ({ delay, immediate }, callback) => {
  let timeoutRef = null;
  let initialFetch = false;
  const debouncedFn = (...args) => {
    const invoke = resolve => {
      resolve(callback(...args));
    };

    if (!timeoutRef && immediate && !initialFetch) {
      return new Promise(resolve => {
        initialFetch = true;
        invoke(resolve);
      });
    }

    clearTimeout(timeoutRef);

    return new Promise(resolve => {
      timeoutRef = setTimeout(() => invoke(resolve), delay);
    });
  };

  return debouncedFn;
};

export default useDebounce;
